import { REFERRAL_PROOF_PARAM, REFERRAL_TOKEN_PARAM } from "./magic-link";

/**
 * Where an emailed magic link lands. Mail scanners open every link they find,
 * and Better Auth spends the token on the first GET to its verify endpoint, so
 * the email points here instead and only a click on this page reaches verify.
 */
export const MAGIC_LINK_INTERSTITIAL_PATH = "/auth/magic-link";

const VERIFY_PATH = "/api/auth/magic-link/verify";

// What the verify endpoint and our referral handoff read from the link. Any
// other param on it is dropped.
const VERIFY_PARAMS = [
  "token",
  "callbackURL",
  "newUserCallbackURL",
  "errorCallbackURL",
  REFERRAL_TOKEN_PARAM,
  REFERRAL_PROOF_PARAM,
] as const;

function pickVerifyParams(params: URLSearchParams): URLSearchParams {
  const picked = new URLSearchParams();
  for (const name of VERIFY_PARAMS) {
    const value = params.get(name);
    if (value) {
      picked.set(name, value);
    }
  }
  return picked;
}

/**
 * The same-origin path Continue navigates to, or null for a link without a
 * token. Built from a fixed path and the allowed params: nothing in the link
 * can name a different path or host.
 */
export function magicLinkVerifyPath(params: URLSearchParams): string | null {
  const verify = pickVerifyParams(params);
  return verify.has("token") ? `${VERIFY_PATH}?${verify}` : null;
}

/**
 * The URL the email carries in place of Better Auth's verify URL, with the
 * same params moved into the fragment. A URL without a token is returned as it
 * is, as withMagicLinkReferral does.
 */
export function magicLinkInterstitialUrl(verifyUrl: string): string {
  const url = new URL(verifyUrl);
  const params = pickVerifyParams(url.searchParams);
  return params.has("token")
    ? `${url.origin}${MAGIC_LINK_INTERSTITIAL_PATH}#${params}`
    : verifyUrl;
}

// The params travel in the fragment, as the email-change link's token does:
// it never reaches an access log, the attribution cookie the proxy writes from
// the landing URL, or a Referer. Analytics still reads location.href, so it is
// lifted before they initialise and kept only in module memory, never the DOM.
let parkedPath: string | null = null;

function liftPathFromAddressBar(): string | null {
  const { pathname, search, hash } = window.location;
  if (pathname !== MAGIC_LINK_INTERSTITIAL_PATH || hash.length < 2) {
    return null;
  }

  const path = magicLinkVerifyPath(new URLSearchParams(hash.slice(1)));
  if (path === null) {
    return null;
  }

  window.history.replaceState(window.history.state, "", `${pathname}${search}`);
  return path;
}

/** Runs from `instrumentation-client`, before analytics initialise. */
export function captureMagicLinkVerifyPath(): void {
  const path = liftPathFromAddressBar();
  if (path !== null) {
    parkedPath = path;
  }
}

// Hash-only navigation does not rerun instrumentation-client.
export function takeMagicLinkVerifyPath(): string | null {
  const path = liftPathFromAddressBar() ?? parkedPath;
  parkedPath = null;
  return path;
}
